import { MapPin, Sparkles, Heart, Award, Check, Users, Shield } from 'lucide-react';
import type { City } from '@/data/cities';

interface Props { city: City }

const highlights = [
  { icon: MapPin, title: 'Proximité terrain', text: 'Basés à Troyes, nous connaissons parfaitement le territoire et ses meilleurs prestataires.' },
  { icon: Sparkles, title: 'Événements sur-mesure', text: 'Mariages, galas, séminaires, anniversaires, festivals — chaque projet est unique.' },
  { icon: Heart, title: 'Accompagnement humain', text: 'Un suivi passionné et bienveillant, du premier échange au lendemain du jour J.' },
  { icon: Award, title: 'Expertise reconnue', text: '25 ans d\'expérience, 250+ événements, 4.9/5 sur Google avec 32 avis.' },
];

const engagements = [
  'Premier rendez-vous gratuit et sans engagement',
  'Devis détaillé et transparent sous 48 à 72h',
  'Interlocuteur unique du premier appel au jour J',
  'Réseau de 100+ prestataires qualifiés',
  'Présence et coordination complète le jour J',
  'Budget maîtrisé — pas de mauvaise surprise',
];

const VilleIntro = ({ city }: Props) => (
  <section className="py-16 md:py-20 px-6 lg:px-8 bg-background">
    <div className="max-w-5xl mx-auto">
      {/* Quote block */}
      <div className="p-6 md:p-8 rounded-2xl bg-muted/40 border border-border/50 mb-12">
        <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
          Vous recherchez une agence événementielle près de {city.name} ? <strong className="text-foreground">Cygnature Production</strong>, basée à Troyes, est l'adresse incontournable pour tous vos projets événementiels {city.prefix} {city.name} et ses alentours.
        </p>
      </div>

      {/* 4 highlight cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-12">
        {highlights.map((h, i) => {
          const Icon = h.icon;
          return (
            <div key={i} className="relative flex items-start gap-4 p-5 rounded-xl border border-border bg-card overflow-hidden hover:border-primary/20 hover:shadow-sm transition-all">
              <div className="absolute top-0 left-0 bottom-0 w-1 bg-gradient-to-b from-primary to-primary/30 rounded-l-xl" />
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center shrink-0 ml-1">
                <Icon size={18} className="text-primary" strokeWidth={1.5} />
              </div>
              <div>
                <p className="text-sm font-heading font-bold text-foreground mb-0.5">{h.title}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{h.text}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Two-column intro text with bullet points extracted */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <h2 className="font-heading text-xl md:text-2xl font-black text-petrol mb-4">
            {city.name} : un territoire que nous connaissons
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">{city.introLocal}</p>
        </div>
        <div>
          <h2 className="font-heading text-xl md:text-2xl font-black text-petrol mb-4">
            Pourquoi nous choisir {city.prefix} {city.name} ?
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed mb-4">{city.introWhyUs}</p>
        </div>
      </div>

      {/* Engagements list */}
      <div className="mt-12 p-6 md:p-8 rounded-2xl bg-card border border-border">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/10 flex items-center justify-center">
            <Shield size={18} className="text-primary" strokeWidth={1.5} />
          </div>
          <h3 className="font-heading font-bold text-foreground">Nos engagements {city.prefix} {city.name}</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {engagements.map((e, i) => (
            <div key={i} className="flex items-start gap-2.5">
              <Check size={14} className="text-primary mt-0.5 shrink-0" />
              <span className="text-sm text-muted-foreground">{e}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Address block */}
      <div className="mt-6 flex items-center gap-4 p-5 rounded-xl bg-card border border-border">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
          <MapPin size={22} className="text-primary" strokeWidth={1.5} />
        </div>
        <div>
          <p className="font-heading font-bold text-foreground text-sm">8 Rue du Champ de la Gaule, 10120 Saint-Pouange</p>
          <p className="text-xs text-muted-foreground">À {city.distanceKm} km de {city.name} — {city.driveTime} en voiture</p>
        </div>
      </div>
    </div>
  </section>
);

export default VilleIntro;
